import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { StatusDot } from '@/components/ui/StatusDot'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { ReportTemplatesService, type ReportTemplate } from '@/services/modules/ReportTemplatesService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

const BUILTIN_LABELS: Record<string, string> = {
  medicar: 'Medicar (CSV)',
  'porto-seguro': 'Porto Seguro',
  auster: 'Auster',
}

/**
 * /settings/commercial/report-templates — lista de modelos de relatório
 * configuráveis por prestador. Cada modelo define colunas, formato (Excel/CSV)
 * e a regra de família. A geração do arquivo fica dentro do editor do modelo.
 */
export function SettingsReportTemplatesPage() {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.report_templates.create')
  const canEdit = can('settings.report_templates.edit')
  const canDelete = can('settings.report_templates.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar modelo' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(ReportTemplatesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<ReportTemplate | null>(null)

  const exp = useDataTableExport({
    filename: 'modelos-relatorio',
    service: ReportTemplatesService,
    filters: sf.filters,
    columns: [
      { header: 'Modelo',      accessor: (t: ReportTemplate) => t.name },
      { header: 'Prestadores', accessor: (t: ReportTemplate) => t.serviceProviders?.length ? t.serviceProviders.map(p => p.name).join(', ') : 'Nenhum' },
      { header: 'Tipo',        accessor: (t: ReportTemplate) => t.builtinKey ? (BUILTIN_LABELS[t.builtinKey] ?? t.builtinKey) : 'Personalizado' },
      { header: 'Status',      accessor: (t: ReportTemplate) => t.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try {
      await ReportTemplatesService.destroy(openDelete.id)
      toast.success('Modelo removido.')
      setOpenDelete(null); setReload(v => v + 1)
    } catch { toast.error('Erro ao remover.') }
  }

  const columns: DataTableColumn<ReportTemplate>[] = [
    { key: 'name', header: 'Modelo', sortable: true, alwaysVisible: true, render: t => <span className="font-medium text-gray-800">{t.name}</span> },
    { key: 'providers', header: 'Prestadores', render: t => (t.serviceProviders?.length ? t.serviceProviders.map(p => p.name).join(', ') : <span className="text-gray-400 italic">Nenhum</span>) },
    { key: 'type', header: 'Tipo', align: 'center', className: 'w-40', render: t => (
      t.builtinKey
        ? <Badge variant="blue">{BUILTIN_LABELS[t.builtinKey] ?? t.builtinKey}</Badge>
        : <Badge variant="gray">Personalizado</Badge>
    ) },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: t => <StatusDot value={t.status} /> },
  ]

  return (
    <>
      <PageHeader
        title="Modelos de relatório"
        subtitle="Layouts de exportação configuráveis por prestador"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Modelos de relatório' }]}
        actions={canCreate ? <Button onClick={() => navigate('/settings/commercial/report-templates/novo')} iconLeft={<Plus size={14} />}>Novo modelo</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.reporttemplates"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={t => t.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(t) => [
          // Sem `edit`, o editor abre só para consulta (o Salvar fica desabilitado lá).
          canEdit
            ? { key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/settings/commercial/report-templates/${t.id}/editar`) }
            : { key: 'view', label: 'Visualizar', icon: <Eye size={14} />, onClick: () => navigate(`/settings/commercial/report-templates/${t.id}/editar`) },
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setOpenDelete(t) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover modelo de relatório?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? As colunas e regras associadas também serão removidas. Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
